import React from 'react';
import { Chart } from 'chart.js';

import {
   formatChartNumber,
   MAIN_ANALYTICS_CHART_COLORS,
} from './chart.constants';
import { registerMainAnalyticsCharts } from './register-main-analytics-charts';

type BarChartProps = {
   label: string;
   labels: string[];
   values: number[];
   colors?: string[];
};

export const BarChart = ({
   label,
   labels,
   values,
   colors = MAIN_ANALYTICS_CHART_COLORS,
}: BarChartProps) => {
   const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

   React.useEffect(() => {
      registerMainAnalyticsCharts();

      const canvas = canvasRef.current;
      if (!canvas) {
         return undefined;
      }

      const chart = new Chart(canvas, {
         type: 'bar',
         data: {
            labels,
            datasets: [
               {
                  label,
                  data: values,
                  backgroundColor: labels.map((_, index) => colors[index % colors.length]),
                  borderRadius: 4,
                  maxBarThickness: 56,
               },
            ],
         },
         options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
               legend: {
                  display: false,
               },
               tooltip: {
                  callbacks: {
                     label: (item) => `${label}: ${formatChartNumber(Number(item.raw ?? 0))}`,
                  },
               },
            },
            scales: {
               x: {
                  ticks: {
                     color: '#202020',
                     maxRotation: 0,
                  },
                  grid: {
                     display: false,
                  },
               },
               y: {
                  beginAtZero: true,
                  ticks: {
                     color: '#555555',
                  },
               },
            },
         },
      });

      return () => chart.destroy();
   }, [colors, label, labels, values]);

   return <canvas ref={canvasRef} role="img" aria-label={label} />;
};
